import { GeorefData, QueuedIntakeFileInfo, UploadData } from '../../../store/uploadStore';
import { UploadNotRestoredError } from '../../../errors/UploadNotRestoredError';
import { createCanceledError } from '../../../errors/createCanceledError';
import { CustomerId, Upload, ValidationWorkerEventPayload } from '../../../types';
import { validationWorkerManager } from './ValidationWorkerManager';
import { UploadType } from '../../../consts/uploadType';
import { sendValidationTimeSeconds, trackValidationStart } from './metrics/sendValidationTimeSeconds';
import { sendValidationMetrics } from './metrics/sendValidationMetrics';
import logger from '../../../libs/logger/Logger';
import { validationResultsToPilotApiMetrics } from '../../../api_services/PilotApiService';
import { GeorefNotSetError } from '../../../errors/GeorefNotSetError';
import { ValidationCheck } from '../../../consts/validationCheck';
import * as Sentry from '@sentry/browser';
import { passingBlurScore } from './passingBlurScore';

// @TODO Why do we send this? We should handle null or undefined on the backend.
// @TODO What if takenAt (dateTime) fails and returns undefined?
const UNKNOWN_CAMERA = 'NO CAMERA';

/**
 * Validate an image file against configured validation checks
 */
export const validateImage = async (
  file: QueuedIntakeFileInfo,
  upload: UploadData,
  georef: GeorefData | undefined,
): Promise<QueuedIntakeFileInfo> => {
  if (!upload.upload) {
    throw new UploadNotRestoredError();
  }

  const abortSignal = upload.meta.abortController.signal;
  const validationChecks = getValidationChecks(upload.meta.type, upload.upload.customer);

  if (file.file.size < 1024) {
    return markFileAsEmpty(file, validationChecks);
  }

  if (typeof Worker === 'undefined') {
    return handleUnsupportedBrowsers(file, validationChecks, upload.meta.abortController.signal);
  }

  if (upload.meta.type === UploadType.Ppk && !georef) throw new GeorefNotSetError();

  trackValidationStart(upload);

  try {
    const payload = generateValidationPayload(file, validationChecks, upload, georef, upload.upload);

    const response = await validationWorkerManager.validate(payload, abortSignal);

    logger.info('Image validation result', response.payload);
    sendValidationMetrics(upload, response.payload);

    const { dateTime, datePrecise, droneModel, photoCoords, validations } = response.payload;

    file.takenAt = dateTime;
    file.datePrecise = datePrecise;
    file.cameraModel = droneModel || UNKNOWN_CAMERA;
    file.coords = photoCoords ? { lon: photoCoords[0], lat: photoCoords[1] } : undefined;
    file.validation = validationResultsToPilotApiMetrics(validations);
    file.isBeingValidated = false;

    return file;
  } catch (error) {
    if (abortSignal.aborted) {
      throw createCanceledError();
    }
    console.error('Validation failed with an error', error);
    Sentry.captureException(error);

    return markImageValidationFailed(file, validationChecks);
  } finally {
    if (upload.queue.every(({ validation }) => validation)) {
      sendValidationTimeSeconds(upload);
    }
  }
};

const validationsByUploadType = {
  [UploadType.RegularAndRtk]: Validation.scanFlowValidations,
  [UploadType.Ppk]: Validation.ppkScanFlowValidations,
  [UploadType.Lidar]: Validation.lidarScanFlowValidations,
  [UploadType.Pano]: Validation.panoFlowValidations,
};

/**
 * Whether date checking is switched off for this customer. Covers every date-derived check, including
 * the ones computed outside the per-flow validation config.
 */
export const shouldSkipDateChecks = (customerId: CustomerId) =>
  Validation.skipDateCheckForCustomers?.includes(customerId) ?? false;

const getValidationChecks = (uploadType: UploadType, customerId: CustomerId) => {
  const validationChecks = validationsByUploadType[uploadType];

  const shouldSkipDateCheck = shouldSkipDateChecks(customerId);

  return validationChecks.filter((check) => {
    if (shouldSkipDateCheck && check === 'photoAgeHours') return false;

    return true;
  });
};

const markFileAsEmpty = (file: QueuedIntakeFileInfo, validationChecks: ValidationCheck[]): QueuedIntakeFileInfo => {
  file.coords = undefined;
  file.cameraModel = UNKNOWN_CAMERA;
  file.takenAt = undefined;
  file.datePrecise = false;
  file.validation = { [ValidationCheck.EmptyFile]: [false, 0] };
  if (validationChecks.includes(ValidationCheck.BlurScore)) {
    file.validation[ValidationCheck.BlurScore] = passingBlurScore(Validation.config.blurScoreThreshold);
  }
  file.isBeingValidated = false;

  return file;
};

export const markImageValidationFailed = (file: QueuedIntakeFileInfo, validationChecks: ValidationCheck[]) => {
  file.coords = undefined;
  file.cameraModel = UNKNOWN_CAMERA; // @TODO should we send undefined here?
  file.takenAt = file.file.lastModified ? new Date(file.file.lastModified).toISOString() : new Date().toISOString(); // @TODO should we send undefined here?
  file.datePrecise = false; // the mtime above is not a photo timestamp, so it must not set the upload date
  file.validation = validationChecks.reduce((acc, validation) => {
    acc[validation] =
      validation === ValidationCheck.BlurScore ? passingBlurScore(Validation.config.blurScoreThreshold) : [false, null];
    return acc;
  }, {});
  file.isBeingValidated = false;

  return file;
};

const generateValidationPayload = (
  file: QueuedIntakeFileInfo,
  validationChecks: ValidationCheck[],
  upload: UploadData,
  georef: GeorefData | undefined,
  uploadRecord: Upload,
): ValidationWorkerEventPayload => {
  if (!upload.meta.timezone) {
    throw new Error('Site timezone not set');
  }

  return {
    messageType: 'validate',
    photo: file.file,
    timezone: upload.meta.timezone, // This timezone will be assigned to naive EXIF / XMP dates
    ppkGeorefCoords: upload.meta.type === UploadType.Ppk && georef ? georef.georef?.[file.file.name] : undefined,
    validations: validationChecks,
    validationConfig: Validation.config,
    // Not derivable from `validations`: PhotoFromFuture is not listed in any per-flow config.
    skipDateChecks: shouldSkipDateChecks(uploadRecord.customer),
    validationPayload: {
      siteBoundary: upload.meta.site.boundary,
      // The date the photos are measured against. Normally the upload's own (scheduled) date, so that a
      // dataset belonging to another date still fails the check. The dataset's own date takes over only
      // where the pilot overrode an unverifiable one, since their answer disagrees with every photo in
      // the set by construction.
      expectedPhotoCreationDate: uploadRecord.expectedPhotoDate ?? upload.overview.date,
    },
  };
};

/**
 * Mark validation failed in browsers that don't support Web Workers
 * Each validation causes a React rerender and the upload loop rerun, so to avoid blocking the main thread for too
 * long we will simply use a setTimeout here.
 */
const handleUnsupportedBrowsers = (
  file: QueuedIntakeFileInfo,
  validationChecks: ValidationCheck[],
  abortSignal: AbortSignal,
): Promise<QueuedIntakeFileInfo> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (abortSignal.aborted) {
        reject(createCanceledError());
        return;
      }
      console.error('Validation failed because workers are not supported in this browser');
      resolve(markImageValidationFailed(file, validationChecks));
    }, 30);
  });
};
